import type {Options, SeriesOptionsType} from 'highcharts';
import CubeIndex, {AxisItem} from '../cube/CubeIndex';
import {categoryLabel, escapeHtml, formatValue, tooltipHtml} from '../render/format';
import {isLowBase, lowBaseAttributes} from '../render/lowBase';
import {MarkRenderer, MEASURES_CHANNEL, RenderContext} from '../types';

type Side = 'negative' | 'neutral' | 'positive';

// Fallback Likert colors (member.color wins); intensity grows with distance from the neutral middle.
const NEGATIVE_SHADES = ['#B71C1C', '#E57373', '#F4B3AE'];
const POSITIVE_SHADES = ['#A8C8E8', '#5B9BD5', '#1F5FA8'];
const NEUTRAL_COLOR = '#BDBDBD';

function shade(shades: string[], rank: number, count: number): string {
    return shades[count <= 1 ? Math.floor(shades.length / 2) : Math.round(rank * (shades.length - 1) / (count - 1))];
}

/**
 * Neutral answers are split half/half around zero (the usual diverging stacked bar): they keep the bar centred on the
 * neutral middle and their full share stays readable. Missing/total members of the series dimension are no answers
 * and are left out.
 */
const renderDiverging: MarkRenderer = (cube, spec, ctx: RenderContext) => {
    const index = new CubeIndex(cube);
    const yChannel = typeof spec.encoding.y === 'string' ? spec.encoding.y : MEASURES_CHANNEL;
    const seriesChannel = typeof spec.encoding.series === 'string' ? spec.encoding.series : '$values';
    const yItems = index.items(yChannel);
    const answers = index.items(seriesChannel).filter(item => !item.member?.role);
    const negativeKeys: string[] = Array.isArray(spec.options.negative) ? spec.options.negative.map(String) : [];
    const neutralKey: string | null = spec.options.neutral === null || spec.options.neutral === undefined
        ? null
        : String(spec.options.neutral);

    const sideOf = (item: AxisItem): Side => negativeKeys.includes(item.id)
        ? 'negative'
        : item.id === neutralKey ? 'neutral' : 'positive';
    const negatives = answers.filter(item => sideOf(item) === 'negative');
    const positives = answers.filter(item => sideOf(item) === 'positive');
    const neutral = answers.find(item => sideOf(item) === 'neutral');

    const colorOf = (item: AxisItem): string => {
        if (item.member?.color) {
            return item.member.color;
        }
        const side = sideOf(item);
        return side === 'neutral'
            ? NEUTRAL_COLOR
            : side === 'negative'
                // negatives are ordered from the far end to the middle
                ? shade(NEGATIVE_SHADES, negatives.indexOf(item), negatives.length)
                : shade(POSITIVE_SHADES, positives.indexOf(item), positives.length);
    };

    const makeSeries = (item: AxisItem, sign: 1 | -1, factor: number, linked: boolean): SeriesOptionsType => {
        const color = colorOf(item);
        const data = yItems.map(yItem => {
            const measure = yItem.measure ?? item.measure ?? cube.measures[0];
            const positions: Record<string, number> = {[seriesChannel]: item.index};
            if (yChannel !== MEASURES_CHANNEL) {
                positions[yChannel] = yItem.index;
            }
            const cell = index.value(positions, measure.id);
            const lowBase = isLowBase(cube, cell);

            return {
                y: cell?.value === null || cell?.value === undefined ? null : sign * factor * cell.value,
                ...(lowBase ? lowBaseAttributes('bar', color, ctx) : {}),
                custom: {
                    count: cell?.count ?? 0,
                    lowBase,
                    // the right half of a split neutral answer must not repeat the label
                    valueText: linked ? '' : formatValue(cell?.value, measure, ctx),
                    tooltip: tooltipHtml({heading: [yItem.label, item.label], color, measure, cell, lowBase}, ctx),
                },
            };
        });

        return {
            id: linked ? `${item.id}:right` : item.id,
            name: escapeHtml(item.label),
            color,
            legendIndex: answers.indexOf(item),
            ...(linked ? {linkedTo: ':previous'} : {}),
            data,
        } as SeriesOptionsType;
    };

    // With reversedStacks: false stacking goes outwards from zero in series order, so negatives are reversed
    // (first answer ends up leftmost).
    const series: SeriesOptionsType[] = [
        ...(neutral ? [makeSeries(neutral, -1, 0.5, false)] : []),
        ...[...negatives].reverse().map(item => makeSeries(item, -1, 1, false)),
        ...(neutral ? [makeSeries(neutral, 1, 0.5, true)] : []),
        ...positives.map(item => makeSeries(item, 1, 1, false)),
    ];

    const unit = escapeHtml(cube.measures[0]?.unit ?? '');

    return {
        type: 'highcharts',
        options: {
            chart: {type: 'bar'},
            title: {text: undefined},
            credits: {enabled: false},
            xAxis: {categories: yItems.map(categoryLabel)},
            yAxis: {
                title: {text: undefined},
                // a row adds up to 100 %; without the bounds the stacked axis auto-pads to +-150
                // Highcharts stacks the last series at the base by default, which would put the strongest answers at zero
                reversedStacks: false,
                min: -100,
                max: 100,
                labels: {
                    formatter(): string {
                        return `${Math.abs(Number(this.value))}${unit}`;
                    },
                },
                plotLines: [{value: 0, width: 1, color: ctx.dark ? '#AAAAAA' : '#555555', zIndex: 5}],
            },
            legend: {enabled: true},
            tooltip: {shared: false, headerFormat: '', pointFormat: '{point.custom.tooltip}'},
            plotOptions: {
                series: {
                    stacking: 'normal',
                    dataLabels: {enabled: spec.options.dataLabels === true, format: '{point.custom.valueText}'},
                },
                // a zero answer share must not show up as a sliver around the axis
                bar: {minPointLength: 0},
            },
            series,
        } as Options,
    };
};

export default renderDiverging;
